import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@clerk/react";
import { trackEvent } from "@/lib/analytics";
import {
  Rss,
  Pause,
  Play,
  Trash2,
  Plus,
  Clock,
  RefreshCw,
  Loader2,
  Radio,
  CheckCircle2,
  AlertTriangle,
  Bell,
  BellOff,
  History,
  X,
  OctagonPause,
  LayoutList,
  Share2,
  Zap,
  ZapOff,
  ChevronDown,
  ChevronUp,
  ThumbsUp,
  ImageIcon,
  Video,
  ExternalLink,
  RefreshCcw,
  Link2,
  RotateCcw,
  LogOut,
  Film,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  listSubscriptions,
  updateSubscription,
  deleteSubscription,
  backfillSubscriptionByUrls,
  listSubscriptionEpisodes,
  useListJobs,
  getListJobsQueryKey,
  listSocialPosts,
  updateSocialPost,
  approveSocialPost,
  discardSocialPost,
  listSocialAccounts,
  disconnectSocialAccount,
  retrySocialPost,
  reopenSocialPost,
  getSocialOAuthUrl,
  testSocialAccount,
  updateQuoteVideoPreference,
  useGetCurrentUser,
} from "@workspace/api-client-react";
import type { RssSubscription, RssEpisode, SocialPost, SocialAccount, ListSocialPostsParams, UpdateSubscriptionBodyVibeProfile, UpdateSubscriptionBodyVideoFormat } from "@workspace/api-client-react";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

const ACTIVE_STATUSES = new Set(["queued", "analyzing", "abbreviating"]);
const BACKFILL_COHORT_WINDOW_MS = 15 * 60 * 1000;
const PENDING_POSTS_POLL_MS = 15_000;
const DISPATCHING_POLL_MS = 5_000;

function isActiveStatus(status: string): boolean {
  return ACTIVE_STATUSES.has(status);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "Never";
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

function formatEpisodeDate(dateStr: string | null): string {
  if (!dateStr) return "Unknown date";
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "";
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
}

function modeLabel(mode: string): string {
  switch (mode) {
    case "quick_catchup": return "Quick Catch-Up";
    case "key_quotes": return "Key Quotes";
    case "full_recap": return "Full Recap";
    default: return mode;
  }
}

function socialPostMediaUrl(type: "thumbnail" | "video", id: number): string {
  return `${basePath}/api/social/posts/${id}/${type}`;
}

// ── Platform icons (inline SVG) ───────────────────────────────────────────────

function YouTubeIcon({ size = 16, className = "" }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}

function LinkedInIcon({ size = 16, className = "" }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function PlatformIcon({ platform, size = 14 }: { platform: string; size?: number }) {
  if (platform === "youtube") return <YouTubeIcon size={size} className="text-red-500" />;
  if (platform === "linkedin") return <LinkedInIcon size={size} className="text-blue-500" />;
  return <Link2 size={size} />;
}

// ── PendingPostCard ────────────────────────────────────────────────────────────

interface PendingPostCardProps {
  post: SocialPost;
  onApprove: (id: number) => Promise<void>;
  onDiscard: (id: number) => Promise<void>;
  onUpdate: (id: number, title: string, desc: string) => Promise<void>;
  onRetry: (id: number) => Promise<void>;
  onReopen?: (id: number) => Promise<void>;
  actioningId: number | null;
}

function PendingPostCard({ post, onApprove, onDiscard, onUpdate, onRetry, onReopen, actioningId }: PendingPostCardProps) {
  const [title, setTitle] = useState(post.postTitle ?? "");
  const [desc, setDesc] = useState(post.postDescription ?? "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showThumb, setShowThumb] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const dirty = title !== (post.postTitle ?? "") || desc !== (post.postDescription ?? "");

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(post.id, title, desc);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const isActioning = actioningId === post.id;
  const isFailed = post.status === "failed";
  const isPartial = post.status === "partial";

  // For partial posts, compute which platforms will be retried (those without a saved URL)
  const failedPlatformNames = isPartial
    ? [
        !post.youtubeUrl ? "YouTube" : null,
        !post.linkedinUrl ? "LinkedIn" : null,
      ].filter((p): p is string => p !== null)
    : [];
  const isPublished = post.status === "published";
  const isGenerating = post.status === "generating";
  const isDiscarded = post.status === "discarded";
  const isDispatching = post.dispatchStatus === "dispatching";
  const isApprovedWaiting = post.status === "approved" && !isDispatching;
  const isPending = post.status === "pending_approval";
  const isReadOnly = isPublished || isGenerating || isDiscarded || isApprovedWaiting;

  return (
    <div className={[
      "rounded-lg border p-4 space-y-3",
      isFailed ? "border-destructive/40 bg-destructive/5"
        : isPartial ? "border-amber-500/40 bg-amber-500/5"
        : isPublished ? "border-green-500/40 bg-green-500/5"
        : isDiscarded ? "border-muted/60 bg-muted/10 opacity-75"
        : isGenerating ? "border-primary/20 bg-primary/5"
        : isApprovedWaiting ? "border-blue-500/30 bg-blue-500/5"
        : "border-border/50 bg-card/60",
    ].join(" ")}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {isGenerating ? (
            <Badge variant="outline" className="text-xs font-mono shrink-0 text-primary border-primary/50 flex items-center gap-1">
              <Loader2 size={9} className="animate-spin" />
              Generating…
            </Badge>
          ) : isDiscarded ? (
            <Badge variant="outline" className="text-xs font-mono shrink-0 text-muted-foreground border-border/60 flex items-center gap-1">
              <X size={9} />
              Discarded
            </Badge>
          ) : isApprovedWaiting ? (
            <Badge variant="outline" className="text-xs font-mono shrink-0 text-blue-600 dark:text-blue-400 border-blue-500/40 flex items-center gap-1">
              <CheckCircle2 size={9} />
              Approved
            </Badge>
          ) : isFailed ? (
            <Badge variant="outline" className="text-xs font-mono shrink-0 text-destructive border-destructive/50 flex items-center gap-1">
              <AlertTriangle size={9} />
              Failed
            </Badge>
          ) : isPartial ? (
            <Badge variant="outline" className="text-xs font-mono shrink-0 text-amber-500 border-amber-500/50 flex items-center gap-1">
              <AlertTriangle size={9} />
              Partially published
            </Badge>
          ) : isPublished ? (
            <Badge variant="outline" className="text-xs font-mono shrink-0 text-green-600 dark:text-green-400 border-green-500/50 flex items-center gap-1">
              <CheckCircle2 size={9} />
              Published
            </Badge>
          ) : isDispatching ? (
            <Badge variant="outline" className="text-xs font-mono shrink-0 text-primary border-primary/50 flex items-center gap-1">
              <Loader2 size={9} className="animate-spin" />
              Posting…
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs font-mono shrink-0 text-amber-500 border-amber-500/50">
              Pending
            </Badge>
          )}
          <span className="text-xs text-muted-foreground font-mono truncate">
            Job #{post.jobId}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {post.thumbnailUrl && !isGenerating && (
            <Button
              variant="ghost"
              size="icon"
              className={["h-7 w-7 text-muted-foreground/60 hover:text-foreground", showThumb ? "text-primary" : ""].join(" ")}
              onClick={() => { setShowThumb((v) => !v); setShowVideo(false); }}
              title="Preview thumbnail"
              disabled={isActioning}
            >
              <ImageIcon size={13} />
            </Button>
          )}
          {post.videoUrl && !isGenerating && (
            <Button
              variant="ghost"
              size="icon"
              className={["h-7 w-7 text-muted-foreground/60 hover:text-foreground", showVideo ? "text-primary" : ""].join(" ")}
              onClick={() => { setShowVideo((v) => !v); setShowThumb(false); }}
              title="Preview video"
              disabled={isActioning}
            >
              <Video size={13} />
            </Button>
          )}
          {(isPending || isFailed || isPartial) && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive/60 hover:text-destructive"
              onClick={() => onDiscard(post.id)}
              disabled={isActioning || isDispatching}
              title="Discard post"
            >
              <Trash2 size={13} />
            </Button>
          )}
          {isDiscarded && onReopen && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs px-2.5 gap-1"
              onClick={() => onReopen(post.id)}
              disabled={isActioning}
              title="Move back to pending approval"
            >
              {isActioning ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <RefreshCcw size={11} />
              )}
              Reopen
            </Button>
          )}
          {(isFailed || isPartial) ? (
            <Button
              size="sm"
              variant="outline"
              className={[
                "h-7 text-xs px-2.5 gap-1",
                isPartial
                  ? "border-amber-500/50 text-amber-600 hover:bg-amber-500 hover:text-white"
                  : "border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground",
              ].join(" ")}
              onClick={() => onRetry(post.id)}
              disabled={isActioning}
              title={
                isPartial && failedPlatformNames.length > 0
                  ? `Only ${failedPlatformNames.join(" and ")} will be re-attempted — the platform that already succeeded won't be touched`
                  : undefined
              }
            >
              {isActioning ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <RotateCcw size={11} />
              )}
              {isPartial && failedPlatformNames.length > 0
                ? `Retry ${failedPlatformNames.join(" & ")}`
                : isPartial
                ? "Retry failed"
                : "Retry"}
            </Button>
          ) : isPending && !isDispatching ? (
            <Button
              size="sm"
              className="h-7 text-xs px-2.5 gap-1"
              onClick={() => onApprove(post.id)}
              disabled={isActioning}
            >
              {isActioning ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <ThumbsUp size={11} />
              )}
              Approve
            </Button>
          ) : null}
        </div>
      </div>

      {/* Failed / partial error message */}
      {(isFailed || isPartial) && post.dispatchError && (
        <div className={[
          "flex items-start gap-2 rounded-md px-3 py-2 border",
          isPartial
            ? "bg-amber-500/10 border-amber-500/20"
            : "bg-destructive/10 border-destructive/20",
        ].join(" ")}>
          <AlertTriangle size={12} className={["mt-0.5 shrink-0", isPartial ? "text-amber-500" : "text-destructive"].join(" ")} />
          <div className="text-xs font-mono leading-relaxed space-y-1">
            {isPartial && (post.youtubeUrl || post.linkedinUrl) && (
              <p className="text-amber-600 dark:text-amber-400 font-medium flex flex-wrap items-center gap-x-2">
                <span>Succeeded:</span>
                {post.youtubeUrl && (
                  <a
                    href={post.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-amber-500"
                  >
                    <YouTubeIcon size={11} />
                    YouTube
                    <ExternalLink size={9} />
                  </a>
                )}
                {post.linkedinUrl && (
                  <a
                    href={post.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-amber-500"
                  >
                    <LinkedInIcon size={11} />
                    LinkedIn
                    <ExternalLink size={9} />
                  </a>
                )}
              </p>
            )}
            <p className={isPartial ? "text-amber-700 dark:text-amber-300" : "text-destructive"}>
              Failed: {post.dispatchError}
            </p>
          </div>
        </div>
      )}

      {/* Generating: informational message */}
      {isGenerating && (
        <p className="text-xs text-muted-foreground font-mono">
          AI is generating thumbnail, video, title, and description…
        </p>
      )}

      {/* Published / partial platform links */}
      {(isPublished || isPartial) && (post.youtubeUrl || post.linkedinUrl) && (
        <div className="flex flex-wrap gap-2">
          {post.youtubeUrl && (
            <a
              href={post.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-mono text-red-500 hover:text-red-400 underline underline-offset-2"
            >
              <YouTubeIcon size={12} />
              View on YouTube
              <ExternalLink size={10} />
            </a>
          )}
          {post.linkedinUrl && (
            <a
              href={post.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-mono text-blue-500 hover:text-blue-400 underline underline-offset-2"
            >
              <LinkedInIcon size={12} />
              View on LinkedIn
              <ExternalLink size={10} />
            </a>
          )}
        </div>
      )}

      {/* Thumbnail / Video preview */}
      {showThumb && post.thumbnailUrl && (
        <div className="rounded-md overflow-hidden border border-border/50 aspect-video bg-muted/30">
          <img
            src={socialPostMediaUrl("thumbnail", post.id)}
            alt="Social post thumbnail"
            className="w-full h-full object-cover"
          />
        </div>
      )}
      {showVideo && post.videoUrl && (
        <div className="rounded-md overflow-hidden border border-border/50 aspect-video bg-black">
          <video
            src={socialPostMediaUrl("video", post.id)}
            controls
            className="w-full h-full"
            preload="metadata"
          />
        </div>
      )}

      {/* Editable title/description (only for pending/failed) */}
      {!isReadOnly && !isDispatching && (
        <>
          <div className="space-y-1">
            <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Title</label>
            {editing ? (
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-md border border-border/60 bg-background px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-primary"
                maxLength={120}
                disabled={saving}
              />
            ) : (
              <p
                className="text-sm font-medium cursor-pointer hover:text-primary transition-colors"
                onClick={() => setEditing(true)}
                title="Click to edit"
              >
                {title || <span className="text-muted-foreground italic">No title generated</span>}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Description</label>
            {editing ? (
              <textarea
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                className="w-full rounded-md border border-border/60 bg-background px-3 py-1.5 text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                rows={3}
                maxLength={600}
                disabled={saving}
              />
            ) : (
              <p
                className="text-sm text-muted-foreground leading-relaxed cursor-pointer hover:text-foreground transition-colors"
                onClick={() => setEditing(true)}
                title="Click to edit"
              >
                {desc || <span className="italic">No description generated</span>}
              </p>
            )}
          </div>

          {editing && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="h-7 text-xs px-3"
                onClick={handleSave}
                disabled={saving || !dirty}
              >
                {saving ? <Loader2 size={11} className="animate-spin mr-1" /> : null}
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs px-2"
                onClick={() => {
                  setTitle(post.postTitle ?? "");
                  setDesc(post.postDescription ?? "");
                  setEditing(false);
                }}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          )}
          {!editing && (
            <button
              className="text-xs font-mono text-muted-foreground hover:text-foreground underline underline-offset-2"
              onClick={() => setEditing(true)}
            >
              Edit title &amp; description
            </button>
          )}
        </>
      )}

      {/* Read-only title/description for non-editable states */}
      {isReadOnly && (post.postTitle || post.postDescription) && (
        <div className="space-y-1">
          {post.postTitle && <p className="text-sm font-medium">{post.postTitle}</p>}
          {post.postDescription && (
            <p className="text-xs text-muted-foreground leading-relaxed">{post.postDescription}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function SubscriptionsPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { isSignedIn } = useAuth();
  const { data: currentUser } = useGetCurrentUser();
  const isPro = currentUser?.plan === "pro";

  const [subscriptions, setSubscriptions] = useState<RssSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState<number | null>(null);

  const [pendingDeleteSub, setPendingDeleteSub] = useState<RssSubscription | null>(null);

  const [catchUpId, setCatchUpId] = useState<number | null>(null);
  const [catchUpEpisodes, setCatchUpEpisodes] = useState<RssEpisode[]>([]);
  const [catchUpEpisodesLoading, setCatchUpEpisodesLoading] = useState(false);
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());
  const [catchUpLoadingId, setCatchUpLoadingId] = useState<number | null>(null);

  // Social posts state
  const [pendingPosts, setPendingPosts] = useState<SocialPost[]>([]);
  const [postsExpanded, setPostsExpanded] = useState(true);
  const [postActioningId, setPostActioningId] = useState<number | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dispatchingPollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Social post history state
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [historyPosts, setHistoryPosts] = useState<SocialPost[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyStatusFilter, setHistoryStatusFilter] = useState("all");
  const [historySubFilter, setHistorySubFilter] = useState<string>("");
  const [historySinceFilter, setHistorySinceFilter] = useState<string>("");

  // Social settings open state (one at a time, like catchUpId)
  const [socialSettingsOpenId, setSocialSettingsOpenId] = useState<number | null>(null);
  // Cinematic pre-gen settings open state
  const [cinematicSettingsOpenId, setCinematicSettingsOpenId] = useState<number | null>(null);
  const [socialAccounts, setSocialAccounts] = useState<SocialAccount[]>([]);
  const [socialAccountsLoading, setSocialAccountsLoading] = useState(false);

  // Test connection state — keyed by platform ("youtube" | "linkedin") so results
  // survive account ID churn (e.g. disconnect + reconnect creates a new account row)
  // and persist for the current page session across settings panel open/close cycles.
  const [testingAccountId, setTestingAccountId] = useState<number | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; requiresReconnect: boolean; message: string }>>({});

  // Pending OAuth callback result from URL (handled after accounts are loaded)
  const [pendingOAuthResult] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("socialOAuth");
    if (result) {
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState(null, "", cleanUrl);
    }
    return result;
  });

  useEffect(() => {
    if (catchUpId === null) {
      setCatchUpEpisodes([]);
      setSelectedUrls(new Set());
      return;
    }
    let cancelled = false;
    setCatchUpEpisodes([]);
    setSelectedUrls(new Set());
    setCatchUpEpisodesLoading(true);
    void listSubscriptionEpisodes(catchUpId, { limit: 20 })
      .then((result) => {
        if (cancelled) return;
        setCatchUpEpisodes(result.episodes);
        const preSelected = new Set(
          result.episodes.filter((ep) => !ep.alreadyProcessed).map((ep) => ep.audioUrl),
        );
        setSelectedUrls(preSelected);
      })
      .catch(() => {
        if (!cancelled) {
          toast({ title: "Error", description: "Failed to load episode list", variant: "destructive" });
        }
      })
      .finally(() => {
        if (!cancelled) setCatchUpEpisodesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [catchUpId, toast]);

  const toggleEpisode = (audioUrl: string) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(audioUrl)) {
        next.delete(audioUrl);
      } else {
        next.add(audioUrl);
      }
      return next;
    });
  };

  const selectAllNew = () => {
    setSelectedUrls(new Set(catchUpEpisodes.filter((ep) => !ep.alreadyProcessed).map((ep) => ep.audioUrl)));
  };

  const clearSelection = () => {
    setSelectedUrls(new Set());
  };

  const { data: allJobs } = useListJobs(
    undefined,
    {
      query: {
        queryKey: getListJobsQueryKey(undefined),
        enabled: isSignedIn === true,
        refetchInterval: (query) => {
          const data = query.state.data;
          if (!data) return false;
          const hasActive = (data as { status: string }[]).some((j) => isActiveStatus(j.status));
          return hasActive ? 3000 : false;
        },
      },
    }
  );

  const backfillProgressBySubId = (() => {
    if (!allJobs) return new Map<number, { done: number; total: number }>();
    const bySubscription = new Map<number, typeof allJobs>();
    for (const job of allJobs) {
      if (job.subscriptionId == null) continue;
      if (!bySubscription.has(job.subscriptionId)) {
        bySubscription.set(job.subscriptionId, []);
      }
      bySubscription.get(job.subscriptionId)!.push(job);
    }
    const result = new Map<number, { done: number; total: number }>();
    for (const [subId, subJobs] of bySubscription) {
      const activeJobs = subJobs.filter((j) => isActiveStatus(j.status));
      if (activeJobs.length === 0) continue;
      const earliestActiveTs = Math.min(
        ...activeJobs.map((j) => new Date(j.createdAt).getTime())
      );
      const cohort = subJobs.filter(
        (j) => Math.abs(new Date(j.createdAt).getTime() - earliestActiveTs) <= BACKFILL_COHORT_WINDOW_MS
      );
      const done = cohort.filter((j) => !isActiveStatus(j.status)).length;
      const total = cohort.length;
      result.set(subId, { done, total });
    }
    return result;
  })();

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listSubscriptions();
      setSubscriptions(result.subscriptions);
    } catch {
      toast({ title: "Error", description: "Failed to load subscriptions", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchPendingPosts = useCallback(async () => {
    try {
      const result = await listSocialPosts();
      setPendingPosts(result.posts);
    } catch {
      // silent — don't disrupt the page
    }
  }, []);

  const fetchSocialAccounts = useCallback(async () => {
    setSocialAccountsLoading(true);
    try {
      const result = await listSocialAccounts();
      setSocialAccounts(result.accounts);
    } catch {
      // silent
    } finally {
      setSocialAccountsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSignedIn) {
      void fetchSubscriptions();
      void fetchPendingPosts();
      void fetchSocialAccounts().then(() => {
        if (!pendingOAuthResult) return;
        if (pendingOAuthResult === "youtube_success") {
          setTestResults((prev) => { const next = { ...prev }; delete next["youtube"]; return next; });
          toast({ title: "YouTube connected", description: "Your YouTube account has been linked." });
        } else if (pendingOAuthResult === "linkedin_success") {
          setTestResults((prev) => { const next = { ...prev }; delete next["linkedin"]; return next; });
          toast({ title: "LinkedIn connected", description: "Your LinkedIn account has been linked." });
        } else if (pendingOAuthResult === "youtube_not_configured") {
          toast({ title: "YouTube not configured", description: "YouTube OAuth credentials are not set up on this server.", variant: "destructive" });
        } else if (pendingOAuthResult === "linkedin_not_configured") {
          toast({ title: "LinkedIn not configured", description: "LinkedIn OAuth credentials are not set up on this server.", variant: "destructive" });
        } else if (pendingOAuthResult.includes("youtube")) {
          toast({ title: "YouTube connection failed", description: "Could not connect your YouTube account. Please try again.", variant: "destructive" });
        } else if (pendingOAuthResult.includes("linkedin")) {
          toast({ title: "LinkedIn connection failed", description: "Could not connect your LinkedIn account. Please try again.", variant: "destructive" });
        }
      });
    }
  }, [isSignedIn, fetchSubscriptions, fetchPendingPosts, fetchSocialAccounts, pendingOAuthResult, toast]);

  // Poll for pending posts every 15s
  useEffect(() => {
    if (!isSignedIn) return;
    pollTimerRef.current = setInterval(() => { void fetchPendingPosts(); }, PENDING_POSTS_POLL_MS);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [isSignedIn, fetchPendingPosts]);

  // While any post is actively dispatching, poll at a faster rate (5s) so the
  // UI reflects the dispatching → published/failed transition promptly.
  const hasDispatchingPost = pendingPosts.some((p) => p.dispatchStatus === "dispatching");
  useEffect(() => {
    if (!isSignedIn || !hasDispatchingPost) {
      if (dispatchingPollTimerRef.current) {
        clearInterval(dispatchingPollTimerRef.current);
        dispatchingPollTimerRef.current = null;
      }
      return;
    }
    dispatchingPollTimerRef.current = setInterval(() => { void fetchPendingPosts(); }, DISPATCHING_POLL_MS);
    return () => {
      if (dispatchingPollTimerRef.current) {
        clearInterval(dispatchingPollTimerRef.current);
        dispatchingPollTimerRef.current = null;
      }
    };
  }, [isSignedIn, hasDispatchingPost, fetchPendingPosts]);

  const handleToggle = async (sub: RssSubscription) => {
    setActioningId(sub.id);
    try {
      const updated = await updateSubscription(sub.id, { isActive: !sub.isActive });
      setSubscriptions((prev) =>
        prev.map((s) => (s.id === sub.id ? updated.subscription : s)),
      );
      const wasAutoPaused = !sub.isActive && !!sub.autoPausedAt;
      toast({
        title: sub.isActive ? "Subscription paused" : "Subscription resumed",
        description: wasAutoPaused
          ? `"${sub.feedTitle || sub.feedUrl}" is now active with a fresh start. It will auto-pause again if 10 more failures occur.`
          : `"${sub.feedTitle || sub.feedUrl}" is now ${sub.isActive ? "paused" : "active"}.`,
      });
    } catch {
      toast({ title: "Error", description: "Failed to update subscription", variant: "destructive" });
    } finally {
      setActioningId(null);
    }
  };

  const handleToggleEmail = async (sub: RssSubscription) => {
    setActioningId(sub.id);
    try {
      const updated = await updateSubscription(sub.id, { emailNotifications: !sub.emailNotifications });
      setSubscriptions((prev) =>
        prev.map((s) => (s.id === sub.id ? updated.subscription : s)),
      );
      toast({
        title: sub.emailNotifications ? "Email notifications off" : "Email notifications on",
        description: `You will ${sub.emailNotifications ? "no longer" : "now"} receive emails for new episodes from "${sub.feedTitle || sub.feedUrl}".`,
      });
    } catch {
      toast({ title: "Error", description: "Failed to update notification preference", variant: "destructive" });
    } finally {
      setActioningId(null);
    }
  };

  const handleToggleSocialPosting = async (sub: RssSubscription) => {
    setActioningId(sub.id);
    try {
      const nextValue = !sub.socialPostingEnabled;
      const updated = await updateSubscription(sub.id, { socialPostingEnabled: nextValue });
      setSubscriptions((prev) =>
        prev.map((s) => (s.id === sub.id ? updated.subscription : s)),
      );
      toast({
        title: nextValue ? "Social posting enabled" : "Social posting disabled",
        description: nextValue
          ? `New episodes from "${sub.feedTitle || sub.feedUrl}" will generate social media assets for your review.`
          : `Social post generation is now off for "${sub.feedTitle || sub.feedUrl}".`,
      });
    } catch {
      toast({ title: "Error", description: "Failed to update social posting setting", variant: "destructive" });
    } finally {
      setActioningId(null);
    }
  };

  const handleToggleAutoPost = async (sub: RssSubscription) => {
    setActioningId(sub.id);
    try {
      const nextValue = !sub.autoPostEnabled;
      const updated = await updateSubscription(sub.id, { autoPostEnabled: nextValue });
      setSubscriptions((prev) =>
        prev.map((s) => (s.id === sub.id ? updated.subscription : s)),
      );
      toast({
        title: nextValue ? "Auto-post enabled" : "Auto-post disabled",
        description: nextValue
          ? `Posts for "${sub.feedTitle || sub.feedUrl}" will be dispatched automatically.`
          : `Posts for "${sub.feedTitle || sub.feedUrl}" will require manual approval.`,
      });
    } catch {
      toast({ title: "Error", description: "Failed to update auto-post setting", variant: "destructive" });
    } finally {
      setActioningId(null);
    }
  };

  const handleToggleSocialPostFailureEmails = async (sub: RssSubscription) => {
    setActioningId(sub.id);
    try {
      const nextValue = !sub.socialPostFailureEmails;
      const updated = await updateSubscription(sub.id, { socialPostFailureEmails: nextValue });
      setSubscriptions((prev) =>
        prev.map((s) => (s.id === sub.id ? updated.subscription : s)),
      );
      toast({
        title: nextValue ? "Failure alerts on" : "Failure alerts off",
        description: nextValue
          ? `You will receive emails when social posts for "${sub.feedTitle || sub.feedUrl}" fail.`
          : `You will no longer receive emails when social posts for "${sub.feedTitle || sub.feedUrl}" fail.`,
      });
    } catch {
      toast({ title: "Error", description: "Failed to update social post failure email setting", variant: "destructive" });
    } finally {
      setActioningId(null);
    }
  };

  const handleUpdateCinematicPrefs = async (
    sub: RssSubscription,
    updates: { vibeProfile?: UpdateSubscriptionBodyVibeProfile; videoFormat?: UpdateSubscriptionBodyVideoFormat },
  ) => {
    try {
      const updated = await updateSubscription(sub.id, updates);
      setSubscriptions((prev) =>
        prev.map((s) => (s.id === sub.id ? updated.subscription : s)),
      );
      toast({
        title: "Cinematic preferences saved",
        description: updates.vibeProfile === null
          ? `Cinematic pre-generation turned off for "${sub.feedTitle || sub.feedUrl}".`
          : `"${sub.feedTitle || sub.feedUrl}" will use the ${updates.vibeProfile ?? sub.vibeProfile} style.`,
      });
    } catch {
      toast({ title: "Error", description: "Failed to save cinematic preferences", variant: "destructive" });
    }
  };

  // Cycles through three states: null (inherit) → true (on) → false (off) → null (inherit)
  const handleCycleAutoQuoteVideos = async (sub: RssSubscription) => {
    setActioningId(sub.id);
    const nextValue: boolean | null =
      sub.autoQuoteVideos === null ? true : sub.autoQuoteVideos ? false : null;
    try {
      const updated = await updateSubscription(sub.id, { autoQuoteVideos: nextValue });
      setSubscriptions((prev) =>
        prev.map((s) => (s.id === sub.id ? updated.subscription : s)),
      );
      toast({
        title: nextValue === true ? "Quote videos on" : nextValue === false ? "Quote videos off" : "Using global default",
        description: nextValue === true
          ? `Per-quote short videos will auto-render for new key quote episodes from "${sub.feedTitle || sub.feedUrl}".`
          : nextValue === false
            ? `Quote video auto-generation disabled for "${sub.feedTitle || sub.feedUrl}".`
            : `Quote video setting will follow your global preference.`,
      });
    } catch {
      toast({ title: "Error", description: "Failed to update quote video setting", variant: "destructive" });
    } finally {
      setActioningId(null);
    }
  };

  const handleDelete = (sub: RssSubscription) => {
    setPendingDeleteSub(sub);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteSub) return;
    const sub = pendingDeleteSub;
    setPendingDeleteSub(null);
    setActioningId(sub.id);
    try {
      await deleteSubscription(sub.id);
      setSubscriptions((prev) => prev.filter((s) => s.id !== sub.id));
      toast({ title: "Subscription removed" });
    } catch {
      toast({ title: "Error", description: "Failed to remove subscription", variant: "destructive" });
    } finally {
      setActioningId(null);
    }
  };

  const handleDismissError = async (sub: RssSubscription) => {
    setActioningId(sub.id);
    try {
      const updated = await updateSubscription(sub.id, { acknowledgeError: true });
      setSubscriptions((prev) =>
        prev.map((s) => (s.id === sub.id ? updated.subscription : s)),
      );
    } catch {
      toast({ title: "Error", description: "Failed to dismiss error", variant: "destructive" });
    } finally {
      setActioningId(null);
    }
  };

  const handleCatchUpConfirm = async (sub: RssSubscription) => {
    const urls = Array.from(selectedUrls);
    if (urls.length === 0) return;
    setCatchUpLoadingId(sub.id);
    try {
      const result = await backfillSubscriptionByUrls(sub.id, urls);
      const n = urls.length;
      toast({
        title: "Catch-up queued",
        description: `${n} ${n === 1 ? "episode is" : "episodes are"} being queued for processing from "${sub.feedTitle || sub.feedUrl}".`,
      });
      if (result.warning) {
        setTimeout(() => {
          toast({
            title: "Audio quota notice",
            description: result.warning,
            variant: "default",
          });
        }, 800);
      }
      setCatchUpId(null);
    } catch {
      toast({ title: "Error", description: "Failed to queue catch-up episodes", variant: "destructive" });
    } finally {
      setCatchUpLoadingId(null);
    }
  };

  // ── Social Post Handlers ───────────────────────────────────────────────────

  const syncHistory = (updatedPost: SocialPost) => {
    setHistoryPosts((prev) => prev.map((p) => (p.id === updatedPost.id ? updatedPost : p)));
  };

  const handleApprovePost = async (id: number) => {
    setPostActioningId(id);
    try {
      const result = await approveSocialPost(id);
      setPendingPosts((prev) => prev.map((p) => (p.id === id ? result.post : p)));
      syncHistory(result.post);
      refreshHistoryIfLoaded();
      trackEvent("social_post_approved", { post_id: id });
      toast({ title: "Post approved", description: "Your post is being dispatched to connected platforms." });
    } catch {
      toast({ title: "Error", description: "Failed to approve post", variant: "destructive" });
    } finally {
      setPostActioningId(null);
    }
  };

  const handleDiscardPost = async (id: number) => {
    setPostActioningId(id);
    try {
      await discardSocialPost(id);
      setPendingPosts((prev) => prev.filter((p) => p.id !== id));
      // Post is soft-deleted — optimistically update status in history
      setHistoryPosts((prev) =>
        prev.map((p) => (p.id === id ? { ...p, status: "discarded", dispatchStatus: null, dispatchError: null } : p))
      );
      refreshHistoryIfLoaded();
      toast({ title: "Post discarded" });
    } catch {
      toast({ title: "Error", description: "Failed to discard post", variant: "destructive" });
    } finally {
      setPostActioningId(null);
    }
  };

  const handleUpdatePost = async (id: number, title: string, desc: string) => {
    try {
      const result = await updateSocialPost(id, { postTitle: title, postDescription: desc });
      setPendingPosts((prev) => prev.map((p) => (p.id === id ? result.post : p)));
      syncHistory(result.post);
      toast({ title: "Post updated" });
    } catch {
      toast({ title: "Error", description: "Failed to save post changes", variant: "destructive" });
      throw new Error("Failed to save");
    }
  };

  const handleRetryPost = async (id: number) => {
    setPostActioningId(id);
    try {
      const result = await retrySocialPost(id);
      setPendingPosts((prev) => prev.map((p) => (p.id === id ? result.post : p)));
      syncHistory(result.post);
      refreshHistoryIfLoaded();
      toast({ title: "Retrying post", description: "The post is being dispatched again." });
    } catch {
      toast({ title: "Error", description: "Failed to retry post", variant: "destructive" });
    } finally {
      setPostActioningId(null);
    }
  };

  const handleReopenPost = async (id: number) => {
    setPostActioningId(id);
    try {
      const result = await reopenSocialPost(id);
      setHistoryPosts((prev) => prev.map((p) => (p.id === id ? result.post : p)));
      setPendingPosts((prev) => {
        if (prev.some((p) => p.id === id)) return prev.map((p) => (p.id === id ? result.post : p));
        return [result.post, ...prev];
      });
      refreshHistoryIfLoaded();
      toast({ title: "Post reopened", description: "The post has been moved back to pending approval." });
    } catch {
      toast({ title: "Error", description: "Failed to reopen post", variant: "destructive" });
    } finally {
      setPostActioningId(null);
    }
  };

  const ALL_SOCIAL_POST_STATUSES = "generating,pending_approval,approved,discarded,failed,partial,published";

  const HISTORY_PAGE_SIZE = 20;

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params: ListSocialPostsParams = { limit: HISTORY_PAGE_SIZE, offset: 0 };
      // "all" passes all 6 statuses explicitly so backend doesn't apply its default list
      params.status = historyStatusFilter === "all" ? ALL_SOCIAL_POST_STATUSES : historyStatusFilter;
      if (historySubFilter) params.subscriptionId = parseInt(historySubFilter, 10);
      if (historySinceFilter) params.since = historySinceFilter;
      const result = await listSocialPosts(params);
      setHistoryPosts(result.posts);
      setHistoryTotal(result.total);
      setHistoryLoaded(true);
    } catch {
      toast({ title: "Error", description: "Failed to load post history", variant: "destructive" });
    } finally {
      setHistoryLoading(false);
    }
  }, [historyStatusFilter, historySubFilter, historySinceFilter, toast]);

  // Refresh the history list only when it has already been loaded at least once,
  // so we never trigger a load while the section is still collapsed.
  const historyLoadedRef = useRef(historyLoaded);
  useEffect(() => { historyLoadedRef.current = historyLoaded; }, [historyLoaded]);
  const fetchHistoryRef = useRef(fetchHistory);
  useEffect(() => { fetchHistoryRef.current = fetchHistory; }, [fetchHistory]);

  const refreshHistoryIfLoaded = useCallback(() => {
    if (historyLoadedRef.current) void fetchHistoryRef.current();
  }, []);

  // Refresh history when the browser tab regains focus (if history is already loaded)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refreshHistoryIfLoaded();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [refreshHistoryIfLoaded]);

  const loadMoreHistory = useCallback(async (currentCount: number) => {
    setHistoryLoading(true);
    try {
      const params: ListSocialPostsParams = { limit: HISTORY_PAGE_SIZE, offset: currentCount };
      params.status = historyStatusFilter === "all" ? ALL_SOCIAL_POST_STATUSES : historyStatusFilter;
      if (historySubFilter) params.subscriptionId = parseInt(historySubFilter, 10);
      if (historySinceFilter) params.since = historySinceFilter;
      const result = await listSocialPosts(params);
      setHistoryPosts((prev) => [...prev, ...result.posts]);
      setHistoryTotal(result.total);
    } catch {
      toast({ title: "Error", description: "Failed to load more posts", variant: "destructive" });
    } finally {
      setHistoryLoading(false);
    }
  }, [historyStatusFilter, historySubFilter, historySinceFilter, toast]);

  const handleConnectPlatform = (platform: "youtube" | "linkedin") => {
    const url = getSocialOAuthUrl(platform);
    window.location.href = url;
  };

  const handleDisconnectAccount = async (id: number, platform: string) => {
    try {
      await disconnectSocialAccount(id);
      setSocialAccounts((prev) => prev.filter((a) => a.id !== id));
      setTestResults((prev) => { const next = { ...prev }; delete next[platform]; return next; });
      toast({ title: `${platform === "youtube" ? "YouTube" : "LinkedIn"} disconnected` });
    } catch {
      toast({ title: "Error", description: "Failed to disconnect account", variant: "destructive" });
    }
  };

  const handleTestConnection = async (id: number) => {
    const platform = socialAccounts.find((a) => a.id === id)?.platform;
    if (!platform) return;
    setTestingAccountId(id);
    setTestResults((prev) => { const next = { ...prev }; delete next[platform]; return next; });
    try {
      const result = await testSocialAccount(id);
      setTestResults((prev) => ({ ...prev, [platform]: result }));
      if (result.requiresReconnect) {
        setSocialAccounts((prev) =>
          prev.map((a) => a.id === id ? { ...a, reconnectRequired: true } : a)
        );
      }
    } catch (err) {
      const apiData = (err as { data?: { success?: boolean; requiresReconnect?: boolean; message?: string } })?.data;
      if (apiData?.message) {
        setTestResults((prev) => ({
          ...prev,
          [platform]: {
            success: apiData.success ?? false,
            requiresReconnect: apiData.requiresReconnect ?? false,
            message: apiData.message!,
          },
        }));
        if (apiData.requiresReconnect) {
          setSocialAccounts((prev) =>
            prev.map((a) => a.id === id ? { ...a, reconnectRequired: true } : a)
          );
        }
      } else {
        setTestResults((prev) => ({ ...prev, [platform]: { success: false, requiresReconnect: false, message: "Connection test failed. Please try again." } }));
      }
    } finally {
      setTestingAccountId(null);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-mono font-bold tracking-tight">Feed Subscriptions</h1>
          <p className="text-muted-foreground mt-2 text-sm">
            New episodes from subscribed feeds are automatically queued for processing every hour.
          </p>
        </div>
        <Button
          onClick={() => setLocation("/upload?tab=rss")}
          className="shrink-0"
          size="sm"
        >
          <Plus size={15} className="mr-2" />
          Add Feed
        </Button>
      </div>

      {/* ── Pending Posts Section ──────────────────────────────────────────── */}
      {pendingPosts.length > 0 && (
        <Card className="border-2 border-amber-500/40 bg-card/50 backdrop-blur-sm">
          <CardHeader className="pb-4 border-b border-border/10">
            <button
              className="flex items-center justify-between w-full text-left"
              onClick={() => setPostsExpanded((v) => !v)}
            >
              <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
                <Share2 size={16} className="text-amber-500" />
                Social Posts
                <Badge className="font-mono text-xs bg-amber-500/20 text-amber-500 border-amber-500/40">
                  {pendingPosts.length}
                </Badge>
              </CardTitle>
              {postsExpanded ? (
                <ChevronUp size={16} className="text-muted-foreground shrink-0" />
              ) : (
                <ChevronDown size={16} className="text-muted-foreground shrink-0" />
              )}
            </button>
          </CardHeader>
          {postsExpanded && (
            <CardContent className="pt-4 space-y-3">
              <p className="text-xs text-muted-foreground font-mono">
                Review and approve generated social posts. Published posts show links to each platform.
              </p>
              {pendingPosts.map((post) => (
                <PendingPostCard
                  key={post.id}
                  post={post}
                  onApprove={handleApprovePost}
                  onDiscard={handleDiscardPost}
                  onUpdate={handleUpdatePost}
                  onRetry={handleRetryPost}
                  actioningId={postActioningId}
                />
              ))}
            </CardContent>
          )}
        </Card>
      )}

      {/* ── Subscriptions Card ────────────────────────────────────────────── */}
      <Card className="border-2 border-border/50 bg-card/50 backdrop-blur-sm">
        <CardHeader className="pb-4 border-b border-border/10">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
              <Rss size={16} className="text-primary" />
              Active Subscriptions
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchSubscriptions}
              disabled={loading}
              className="h-7 text-xs"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
              <span className="ml-1.5">Refresh</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
              <Loader2 size={18} className="animate-spin" />
              <span className="text-sm">Loading subscriptions…</span>
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
              <div className="h-14 w-14 rounded-full bg-secondary flex items-center justify-center">
                <Radio size={24} className="text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium text-sm">No subscriptions yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Subscribe to a podcast RSS feed and new episodes will be automatically abbreviated.
                </p>
              </div>
              <Button
                onClick={() => setLocation("/upload?tab=rss")}
                size="sm"
                className="mt-2"
              >
                <Plus size={14} className="mr-2" />
                Subscribe to a Feed
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-border/30">
              {subscriptions.map((sub) => (
                <li key={sub.id} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={[
                        "mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0",
                        sub.autoPausedAt
                          ? "bg-orange-500/15 text-orange-500"
                          : sub.failureCount >= 3
                            ? "bg-destructive/15 text-destructive"
                            : sub.isActive
                              ? "bg-primary/15 text-primary"
                              : "bg-secondary text-muted-foreground",
                      ].join(" ")}>
                        {sub.autoPausedAt ? <OctagonPause size={15} /> : sub.failureCount >= 3 ? <AlertTriangle size={15} /> : <Rss size={15} />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-sm leading-snug truncate max-w-xs">
                            {sub.feedTitle || sub.feedUrl}
                          </p>
                          {sub.autoPausedAt ? (
                            <Badge
                              variant="outline"
                              className="text-xs font-mono shrink-0 border-orange-500/60 text-orange-500 flex items-center gap-1"
                            >
                              <OctagonPause size={10} />
                              Auto-paused
                            </Badge>
                          ) : (
                            <Badge
                              variant={sub.isActive ? "default" : "secondary"}
                              className="text-xs font-mono shrink-0"
                            >
                              {sub.isActive ? "Active" : "Paused"}
                            </Badge>
                          )}
                          {!sub.autoPausedAt && sub.failureCount >= 3 && (
                            <span className="flex items-center gap-1.5">
                              <Badge
                                variant="destructive"
                                className="text-xs font-mono shrink-0 flex items-center gap-1"
                              >
                                <AlertTriangle size={10} />
                                Feed error
                              </Badge>
                              {!sub.lastError && (
                                <button
                                  className="text-xs text-muted-foreground font-mono underline underline-offset-2 hover:text-foreground disabled:opacity-50"
                                  disabled={actioningId === sub.id}
                                  onClick={() => handleDismissError(sub)}
                                  title="Dismiss this error warning"
                                >
                                  Dismiss
                                </button>
                              )}
                            </span>
                          )}
                        </div>
                        {sub.feedTitle && (
                          <p className="text-xs text-muted-foreground font-mono mt-0.5 truncate">
                            {sub.feedUrl}
                          </p>
                        )}
                        {sub.autoPausedAt && (
                          <div className="mt-1.5 flex items-start gap-1.5">
                            <OctagonPause size={10} className="mt-0.5 shrink-0 text-orange-500" />
                            <p className="text-xs text-orange-500 font-mono">
                              Automatically paused after {sub.failureCount} consecutive fetch failures.{" "}
                              {sub.lastError && (
                                <span className="text-orange-400/80" title={sub.lastError}>
                                  Last error: {sub.lastError.length > 60 ? sub.lastError.slice(0, 60) + "…" : sub.lastError}
                                  {" "}
                                </span>
                              )}
                              Resume it once the feed is reachable again — the error count will be reset, but it will auto-pause again if 10 more failures occur.
                            </p>
                          </div>
                        )}
                        {!sub.autoPausedAt && sub.failureCount >= 3 && sub.lastError && (
                          <div className="mt-1 flex items-start gap-2">
                            <p className="text-xs text-destructive font-mono flex items-start gap-1 min-w-0">
                              <AlertTriangle size={10} className="mt-0.5 shrink-0" />
                              <span className="truncate" title={sub.lastError}>
                                {sub.lastError}
                              </span>
                            </p>
                            <button
                              className="text-xs text-muted-foreground font-mono underline underline-offset-2 hover:text-foreground shrink-0 disabled:opacity-50"
                              disabled={actioningId === sub.id}
                              onClick={() => handleDismissError(sub)}
                              title="Dismiss this error warning"
                            >
                              Dismiss
                            </button>
                          </div>
                        )}
                        <div className="flex flex-wrap items-center gap-3 mt-1.5">
                          <span className="text-xs text-muted-foreground font-mono">
                            Mode: <span className="text-foreground">{modeLabel(sub.mode)}</span>
                            {sub.mode === "full_recap" && sub.targetPercent != null && (
                              <> · {sub.targetPercent}%</>
                            )}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground font-mono">
                            <Clock size={10} />
                            Checked: {formatDate(sub.lastCheckedAt)}
                          </span>
                          {sub.lastQueuedAt && (
                            <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-mono">
                              <CheckCircle2 size={10} />
                              Last queued: {formatDate(sub.lastQueuedAt)}
                            </span>
                          )}
                        </div>

                        {backfillProgressBySubId.has(sub.id) && (() => {
                          const progress = backfillProgressBySubId.get(sub.id)!;
                          return (
                            <div className="mt-2 flex items-center gap-2">
                              <Loader2 size={11} className="animate-spin text-primary shrink-0" />
                              <span className="text-xs font-mono text-primary">
                                Backfilling {progress.done}/{progress.total} episode{progress.total !== 1 ? "s" : ""}…
                              </span>
                              <div className="flex items-center gap-0.5 shrink-0">
                                {Array.from({ length: progress.total }).map((_, idx) => (
                                  <div
                                    key={idx}
                                    className={`h-1 w-3 rounded-full transition-colors duration-500 ${
                                      idx < progress.done ? "bg-primary" : "bg-primary/20"
                                    }`}
                                  />
                                ))}
                              </div>
                            </div>
                          );
                        })()}

                        {catchUpId === sub.id && (
                          <div className="mt-3 space-y-2.5">
                            {catchUpEpisodesLoading ? (
                              <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono py-2">
                                <Loader2 size={12} className="animate-spin" />
                                Loading episodes from feed…
                              </div>
                            ) : catchUpEpisodes.length === 0 ? (
                              <p className="text-xs text-muted-foreground font-mono">No episodes found in this feed.</p>
                            ) : (
                              <>
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-xs text-muted-foreground font-mono shrink-0">
                                    Select episodes to queue:
                                  </span>
                                  <div className="flex items-center gap-3">
                                    <button
                                      className="text-xs font-mono text-primary hover:underline underline-offset-2 disabled:opacity-50"
                                      onClick={selectAllNew}
                                      disabled={catchUpLoadingId === sub.id}
                                    >
                                      Select new
                                    </button>
                                    <button
                                      className="text-xs font-mono text-muted-foreground hover:underline underline-offset-2 disabled:opacity-50"
                                      onClick={clearSelection}
                                      disabled={catchUpLoadingId === sub.id}
                                    >
                                      Clear
                                    </button>
                                  </div>
                                </div>
                                <div className="max-h-52 overflow-y-auto rounded-md border border-border/50 bg-muted/10 divide-y divide-border/30">
                                  {catchUpEpisodes.map((ep) => (
                                    <label
                                      key={ep.audioUrl}
                                      className={[
                                        "flex items-start gap-2.5 px-3 py-2 transition-colors text-xs",
                                        ep.alreadyProcessed
                                          ? "opacity-50 cursor-default"
                                          : "cursor-pointer hover:bg-muted/30",
                                      ].join(" ")}
                                    >
                                      <input
                                        type="checkbox"
                                        className="mt-0.5 flex-shrink-0 accent-primary"
                                        checked={selectedUrls.has(ep.audioUrl)}
                                        disabled={ep.alreadyProcessed || catchUpLoadingId === sub.id}
                                        onChange={() => toggleEpisode(ep.audioUrl)}
                                      />
                                      <div className="min-w-0 flex-1">
                                        <span className="font-medium leading-snug block truncate">
                                          {ep.title}
                                        </span>
                                        <span className="text-muted-foreground font-mono mt-0.5 flex items-center gap-1.5 flex-wrap">
                                          {formatEpisodeDate(ep.pubDate)}
                                          {ep.durationSeconds != null && (
                                            <span>· {formatDuration(ep.durationSeconds)}</span>
                                          )}
                                          {ep.alreadyProcessed && (
                                            <span className="text-green-600 dark:text-green-400 flex items-center gap-0.5">
                                              <CheckCircle2 size={9} />
                                              processed
                                            </span>
                                          )}
                                        </span>
                                      </div>
                                    </label>
                                  ))}
                                </div>
                                <div className="flex items-center gap-2 pt-0.5">
                                  <Button
                                    size="sm"
                                    className="h-7 text-xs px-3"
                                    disabled={catchUpLoadingId === sub.id || selectedUrls.size === 0}
                                    onClick={() => handleCatchUpConfirm(sub)}
                                  >
                                    {catchUpLoadingId === sub.id ? (
                                      <Loader2 size={12} className="animate-spin mr-1" />
                                    ) : null}
                                    Queue {selectedUrls.size > 0 ? `${selectedUrls.size} ` : ""}episode{selectedUrls.size !== 1 ? "s" : ""}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 text-xs px-2"
                                    disabled={catchUpLoadingId === sub.id}
                                    onClick={() => setCatchUpId(null)}
                                  >
                                    <X size={12} />
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        {/* Cinematic Pre-gen Settings Panel */}
                        {cinematicSettingsOpenId === sub.id && (
                          <div className="mt-3 space-y-3 rounded-md border border-sky-500/30 bg-sky-500/5 p-3">
                            <p className="text-xs font-mono font-medium text-sky-400 uppercase tracking-wider flex items-center gap-1.5">
                              <Film size={11} />
                              Cinematic pre-generation
                            </p>
                            <p className="text-xs text-muted-foreground">
                              When enabled, new episodes automatically generate cinematic beat images in the background so they&apos;re ready when you create a video.
                            </p>

                            {/* Vibe Profile selector */}
                            <div className="space-y-1.5">
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Vibe style</p>
                              <div className="grid grid-cols-2 gap-1.5">
                                {([null, "grounded", "cinematic", "atmospheric"] as const).map((vibe) => (
                                  <button
                                    key={vibe ?? "off"}
                                    className={[
                                      "rounded-md border px-2 py-1.5 text-xs font-mono transition-colors",
                                      sub.vibeProfile === vibe
                                        ? "border-sky-500 bg-sky-500/20 text-sky-300"
                                        : "border-border bg-background text-muted-foreground hover:border-sky-500/50 hover:text-foreground",
                                    ].join(" ")}
                                    onClick={() => handleUpdateCinematicPrefs(sub, { vibeProfile: vibe })}
                                  >
                                    {vibe === null ? "Off" : vibe.charAt(0).toUpperCase() + vibe.slice(1)}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Format selector — only shown when a vibe is selected */}
                            {sub.vibeProfile != null && (
                              <div className="pt-1 border-t border-sky-500/20 space-y-1.5">
                                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Format</p>
                                <div className="grid grid-cols-3 gap-1.5">
                                  {(["portrait", "landscape", "square"] as const).map((fmt) => (
                                    <button
                                      key={fmt}
                                      className={[
                                        "rounded-md border px-2 py-1.5 text-xs font-mono transition-colors",
                                        (sub.videoFormat ?? "portrait") === fmt
                                          ? "border-sky-500 bg-sky-500/20 text-sky-300"
                                          : "border-border bg-background text-muted-foreground hover:border-sky-500/50 hover:text-foreground",
                                      ].join(" ")}
                                      onClick={() => handleUpdateCinematicPrefs(sub, { videoFormat: fmt })}
                                    >
                                      {fmt.charAt(0).toUpperCase() + fmt.slice(1)}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Quote Video auto-gen toggle — key_quotes subscriptions only, Pro-gated */}
                        {sub.mode === "key_quotes" && cinematicSettingsOpenId === sub.id && (
                          <div className="pt-2 border-t border-sky-500/20 space-y-1.5">
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <p className="text-sm font-medium flex items-center gap-1.5">
                                  Auto quote videos
                                  {!isPro && (
                                    <Badge variant="outline" className="text-[10px] px-1 py-0 border-indigo-500/50 text-indigo-400">Pro</Badge>
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {!isPro
                                    ? "Upgrade to Pro to auto-render video clips for each key quote."
                                    : sub.autoQuoteVideos === null
                                      ? "Using global default — click to enable for this feed."
                                      : sub.autoQuoteVideos
                                        ? "Enabled for this feed — click to disable."
                                        : "Disabled for this feed — click to use global default."}
                                </p>
                              </div>
                              {/* 3-state toggle: null (inherit/indeterminate) → true (on) → false (off) → null */}
                              <button
                                className={[
                                  "relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200",
                                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                                  "disabled:cursor-not-allowed disabled:opacity-50",
                                  isPro ? "cursor-pointer" : "cursor-not-allowed opacity-50",
                                  sub.autoQuoteVideos === true
                                    ? "bg-sky-500"
                                    : sub.autoQuoteVideos === null
                                      ? "bg-muted/60 border border-sky-500/30"
                                      : "bg-muted",
                                ].join(" ")}
                                role="switch"
                                aria-checked={sub.autoQuoteVideos === true}
                                title={
                                  sub.autoQuoteVideos === null
                                    ? "Inheriting global default — click to enable"
                                    : sub.autoQuoteVideos
                                      ? "Enabled — click to disable"
                                      : "Disabled — click to inherit global default"
                                }
                                disabled={actioningId === sub.id || !isPro}
                                onClick={() => isPro && void handleCycleAutoQuoteVideos(sub)}
                              >
                                <span
                                  className={[
                                    "pointer-events-none inline-block h-4 w-4 rounded-full shadow-lg ring-0 transition-transform duration-200",
                                    sub.autoQuoteVideos === null ? "bg-white/60" : "bg-white",
                                    sub.autoQuoteVideos === true ? "translate-x-4" : "translate-x-0",
                                  ].join(" ")}
                                />
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Social Post Settings Panel */}
                        {socialSettingsOpenId === sub.id && (
                          <div className="mt-3 space-y-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
                            <p className="text-xs font-mono font-medium text-amber-500 uppercase tracking-wider flex items-center gap-1.5">
                              <Share2 size={11} />
                              Social post settings
                            </p>

                            {/* Enable social posts toggle */}
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <p className="text-sm font-medium">Enable social posts</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  Generate a thumbnail, video, title, and description for each new episode.
                                </p>
                              </div>
                              <button
                                className={[
                                  "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200",
                                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                                  "disabled:cursor-not-allowed disabled:opacity-50",
                                  sub.socialPostingEnabled ? "bg-amber-500" : "bg-muted",
                                ].join(" ")}
                                role="switch"
                                aria-checked={sub.socialPostingEnabled}
                                disabled={actioningId === sub.id}
                                onClick={() => handleToggleSocialPosting(sub)}
                              >
                                <span
                                  className={[
                                    "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform duration-200",
                                    sub.socialPostingEnabled ? "translate-x-4" : "translate-x-0",
                                  ].join(" ")}
                                />
                              </button>
                            </div>

                            {/* Auto-post toggle (only when social posting is enabled) */}
                            {sub.socialPostingEnabled && (
                              <div className="flex items-center justify-between gap-3 pt-1 border-t border-amber-500/20">
                                <div>
                                  <p className="text-sm font-medium">Auto-post (no approval needed)</p>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    Posts go live automatically. When off, each post waits for your review.
                                  </p>
                                </div>
                                <button
                                  className={[
                                    "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200",
                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                                    "disabled:cursor-not-allowed disabled:opacity-50",
                                    sub.autoPostEnabled ? "bg-green-500" : "bg-muted",
                                  ].join(" ")}
                                  role="switch"
                                  aria-checked={sub.autoPostEnabled}
                                  disabled={actioningId === sub.id}
                                  onClick={() => handleToggleAutoPost(sub)}
                                >
                                  <span
                                    className={[
                                      "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform duration-200",
                                      sub.autoPostEnabled ? "translate-x-4" : "translate-x-0",
                                    ].join(" ")}
                                  />
                                </button>
                              </div>
                            )}

                            {/* Failure email alerts toggle (only when social posting is enabled) */}
                            {sub.socialPostingEnabled && (
                              <div className="flex items-center justify-between gap-3 pt-1 border-t border-amber-500/20">
                                <div>
                                  <p className="text-sm font-medium">Failure alert emails</p>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    Receive an email when a social post fails to publish.
                                  </p>
                                </div>
                                <button
                                  className={[
                                    "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200",
                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                                    "disabled:cursor-not-allowed disabled:opacity-50",
                                    sub.socialPostFailureEmails ? "bg-amber-500" : "bg-muted",
                                  ].join(" ")}
                                  role="switch"
                                  aria-checked={sub.socialPostFailureEmails}
                                  disabled={actioningId === sub.id}
                                  onClick={() => handleToggleSocialPostFailureEmails(sub)}
                                >
                                  <span
                                    className={[
                                      "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform duration-200",
                                      sub.socialPostFailureEmails ? "translate-x-4" : "translate-x-0",
                                    ].join(" ")}
                                  />
                                </button>
                              </div>
                            )}

                            {/* Post to: connected accounts */}
                            {sub.socialPostingEnabled && (
                              <div className="pt-1 border-t border-amber-500/20 space-y-2">
                                <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                                  Post to:
                                </p>
                                {socialAccountsLoading ? (
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono">
                                    <Loader2 size={11} className="animate-spin" />
                                    Loading accounts…
                                  </div>
                                ) : (
                                  <div className="space-y-1.5">
                                    {(["youtube", "linkedin"] as const).map((platform) => {
                                      const acct = socialAccounts.find((a) => a.platform === platform);
                                      const needsReconnect = acct?.reconnectRequired;
                                      const isTesting = acct && testingAccountId === acct.id;
                                      const testResult = testResults[platform];
                                      return (
                                        <div key={platform} className="space-y-1">
                                          <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-1.5 text-xs">
                                              <PlatformIcon platform={platform} size={13} />
                                              {acct ? (
                                                needsReconnect ? (
                                                  <span className="text-amber-400 font-mono">
                                                    {acct.accountName ?? platform} — reconnect required
                                                  </span>
                                                ) : (
                                                  <span className="text-foreground font-mono">
                                                    {acct.accountName ?? platform}
                                                  </span>
                                                )
                                              ) : (
                                                <span className="text-muted-foreground font-mono capitalize">{platform}</span>
                                              )}
                                            </div>
                                            {acct && !needsReconnect ? (
                                              <div className="flex items-center gap-2">
                                                <button
                                                  className="text-[10px] font-mono text-amber-500/70 hover:text-amber-400 transition-colors disabled:opacity-40"
                                                  disabled={isTesting ?? false}
                                                  onClick={() => void handleTestConnection(acct.id)}
                                                >
                                                  {isTesting ? (
                                                    <span className="flex items-center gap-1">
                                                      <Loader2 size={9} className="animate-spin" />
                                                      Testing…
                                                    </span>
                                                  ) : "Test connection"}
                                                </button>
                                                <button
                                                  className="text-[10px] font-mono text-muted-foreground/60 hover:text-destructive transition-colors disabled:opacity-40"
                                                  disabled={isTesting ?? false}
                                                  onClick={() => void handleDisconnectAccount(acct.id, acct.platform)}
                                                >
                                                  disconnect
                                                </button>
                                              </div>
                                            ) : (
                                              !testResult?.requiresReconnect && (
                                                <button
                                                  className="text-[10px] font-mono text-amber-400 hover:text-amber-300 transition-colors"
                                                  onClick={() => void handleConnectPlatform(platform)}
                                                >
                                                  {needsReconnect ? "reconnect" : "connect"}
                                                </button>
                                              )
                                            )}
                                          </div>
                                          {acct && !needsReconnect && platform === "linkedin" && !testResult && (
                                            <p className="text-[10px] font-mono pl-4 text-muted-foreground/50">
                                              publishes a visible test post to LinkedIn
                                            </p>
                                          )}
                                          {testResult && (
                                            testResult.requiresReconnect ? (
                                              <div className="pl-4 flex items-start gap-2">
                                                <p className="text-[10px] font-mono text-destructive flex-1">
                                                  ✗ {testResult.message}
                                                </p>
                                                <button
                                                  className="text-[10px] font-mono text-amber-400 hover:text-amber-300 transition-colors shrink-0"
                                                  onClick={() => void handleConnectPlatform(platform)}
                                                >
                                                  reconnect →
                                                </button>
                                              </div>
                                            ) : (
                                              <p className={[
                                                "text-[10px] font-mono pl-4",
                                                testResult.success ? "text-green-500" : "text-destructive",
                                              ].join(" ")}>
                                                {testResult.success ? "✓" : "✗"} {testResult.message}
                                              </p>
                                            )
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground/60 hover:text-foreground"
                        onClick={() => setLocation(`/jobs?subscriptionId=${sub.id}`)}
                        title="View job history for this feed"
                        aria-label="View jobs"
                      >
                        <LayoutList size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={[
                          "h-8 w-8",
                          catchUpId === sub.id
                            ? "text-primary"
                            : "text-muted-foreground/60 hover:text-foreground",
                        ].join(" ")}
                        disabled={actioningId === sub.id}
                        onClick={() => {
                          if (catchUpId === sub.id) {
                            setCatchUpId(null);
                          } else {
                            setCatchUpId(sub.id);
                          }
                        }}
                        title="Catch up on older episodes"
                      >
                        <History size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={[
                          "h-8 w-8",
                          sub.emailNotifications
                            ? "text-muted-foreground hover:text-foreground"
                            : "text-muted-foreground/40 hover:text-muted-foreground",
                        ].join(" ")}
                        disabled={actioningId === sub.id}
                        onClick={() => handleToggleEmail(sub)}
                        title={sub.emailNotifications ? "Turn off email notifications" : "Turn on email notifications"}
                      >
                        {actioningId === sub.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : sub.emailNotifications ? (
                          <Bell size={14} />
                        ) : (
                          <BellOff size={14} />
                        )}
                      </Button>
                      {/* Cinematic Pre-gen Settings Toggle Button */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className={[
                          "h-8 w-8",
                          cinematicSettingsOpenId === sub.id
                            ? "text-sky-400"
                            : sub.vibeProfile != null
                              ? "text-sky-400/70 hover:text-sky-300"
                              : "text-muted-foreground/40 hover:text-muted-foreground",
                        ].join(" ")}
                        disabled={actioningId === sub.id}
                        onClick={() => setCinematicSettingsOpenId(
                          cinematicSettingsOpenId === sub.id ? null : sub.id
                        )}
                        title="Cinematic pre-generation settings"
                        aria-label="Cinematic pre-generation settings"
                      >
                        <Film size={14} />
                      </Button>

                      {/* Social Posts Settings Toggle Button */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className={[
                          "h-8 w-8",
                          socialSettingsOpenId === sub.id
                            ? "text-amber-500"
                            : sub.socialPostingEnabled
                              ? "text-amber-500/70 hover:text-amber-400"
                              : "text-muted-foreground/40 hover:text-muted-foreground",
                        ].join(" ")}
                        disabled={actioningId === sub.id}
                        onClick={() => setSocialSettingsOpenId(
                          socialSettingsOpenId === sub.id ? null : sub.id
                        )}
                        title="Social post settings"
                        aria-label="Social post settings"
                      >
                        <Share2 size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={actioningId === sub.id}
                        onClick={() => handleToggle(sub)}
                        title={sub.isActive ? "Pause subscription" : sub.autoPausedAt ? "Resume — resets error count (will auto-pause again after 10 new failures)" : "Resume subscription"}
                      >
                        {actioningId === sub.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : sub.isActive ? (
                          <Pause size={14} />
                        ) : (
                          <Play size={14} />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        disabled={actioningId === sub.id}
                        onClick={() => handleDelete(sub)}
                        title="Remove subscription"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {subscriptions.length > 0 && (
        <p className="text-xs text-muted-foreground text-center font-mono">
          Feeds are checked automatically every hour. New episodes are queued with your chosen abbreviation mode.
        </p>
      )}

      {/* ── Social Post History Section ───────────────────────────────────── */}
      <Card className="border-2 border-border/50 bg-card/50 backdrop-blur-sm">
        <CardHeader className="pb-4 border-b border-border/10">
          <button
            className="flex items-center justify-between w-full text-left"
            onClick={() => {
              const next = !historyExpanded;
              setHistoryExpanded(next);
              if (next && !historyLoaded) void fetchHistory();
            }}
          >
            <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
              <History size={16} className="text-muted-foreground" />
              Social Post History
              {historyLoaded && (
                <Badge variant="outline" className="font-mono text-xs flex items-center gap-1">
                  {historyLoading && <Loader2 size={9} className="animate-spin" />}
                  {historyPosts.length}
                </Badge>
              )}
            </CardTitle>
            {historyExpanded ? (
              <ChevronUp size={16} className="text-muted-foreground shrink-0" />
            ) : (
              <ChevronDown size={16} className="text-muted-foreground shrink-0" />
            )}
          </button>
        </CardHeader>
        {historyExpanded && (
          <CardContent className="pt-4 space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex-1 min-w-[140px]">
                <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider block mb-1">Status</label>
                <select
                  className="w-full rounded-md border border-border/60 bg-background px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                  value={historyStatusFilter}
                  onChange={(e) => setHistoryStatusFilter(e.target.value)}
                >
                  <option value="all">All statuses</option>
                  <option value="generating">Generating</option>
                  <option value="pending_approval">Pending approval</option>
                  <option value="approved">Approved</option>
                  <option value="discarded">Discarded</option>
                  <option value="failed">Failed</option>
                  <option value="partial">Partially published</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider block mb-1">Subscription</label>
                <select
                  className="w-full rounded-md border border-border/60 bg-background px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                  value={historySubFilter}
                  onChange={(e) => setHistorySubFilter(e.target.value)}
                >
                  <option value="">All subscriptions</option>
                  {subscriptions.map((sub) => (
                    <option key={sub.id} value={String(sub.id)}>
                      {sub.feedTitle || sub.feedUrl}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider block mb-1">Date range</label>
                <select
                  className="w-full rounded-md border border-border/60 bg-background px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                  value={historySinceFilter}
                  onChange={(e) => setHistorySinceFilter(e.target.value)}
                >
                  <option value="">All time</option>
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                </select>
              </div>
              <div className="flex items-end pb-0.5">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-[30px] text-xs px-3 mt-auto"
                  onClick={() => void fetchHistory()}
                  disabled={historyLoading}
                >
                  {historyLoading ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}
                  Apply
                </Button>
              </div>
            </div>

            {/* Results */}
            {historyLoading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground gap-2">
                <Loader2 size={16} className="animate-spin" />
                <span className="text-sm font-mono">Loading history…</span>
              </div>
            ) : !historyLoaded ? null : historyPosts.length === 0 ? (
              <p className="text-xs text-muted-foreground font-mono py-6 text-center">
                No posts match the selected filters.
              </p>
            ) : (
              <div className="space-y-3">
                {historyPosts.map((post) => (
                  <PendingPostCard
                    key={post.id}
                    post={post}
                    onApprove={handleApprovePost}
                    onDiscard={handleDiscardPost}
                    onUpdate={handleUpdatePost}
                    onRetry={handleRetryPost}
                    onReopen={handleReopenPost}
                    actioningId={postActioningId}
                  />
                ))}
                {historyPosts.length < historyTotal && (
                  <div className="flex justify-center pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs px-4"
                      onClick={() => void loadMoreHistory(historyPosts.length)}
                      disabled={historyLoading}
                    >
                      {historyLoading ? (
                        <Loader2 size={12} className="animate-spin mr-1.5" />
                      ) : null}
                      Load more
                      <span className="ml-1 text-muted-foreground">
                        ({historyPosts.length} of {historyTotal})
                      </span>
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {pendingDeleteSub && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPendingDeleteSub(null)}>
          <div className="bg-card border border-border rounded-lg p-6 max-w-sm w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-mono font-bold text-foreground mb-2">Remove subscription?</h3>
            <p className="text-sm text-muted-foreground mb-6">
              <span className="text-foreground font-medium">{pendingDeleteSub.feedTitle || pendingDeleteSub.feedUrl}</span> will be removed. Your existing abbreviations won't be affected.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" className="font-mono uppercase text-xs tracking-wider" onClick={() => setPendingDeleteSub(null)}>
                Cancel
              </Button>
              <Button variant="destructive" className="font-mono uppercase text-xs tracking-wider" onClick={handleConfirmDelete}>
                Remove
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
